import type { BoxHoehe, Ergebnis, FifoRichtung, Katalog, Korbtyp } from '../api/typen'

export interface ErgebnisWerte {
  waagen: string[]
  boxHoehe: BoxHoehe | null
  korbtyp: Korbtyp | null
  fifo: boolean
  fifoRichtung: FifoRichtung | null
  anzahlArtikel: number | null
}

/** Formularwerte aus Vorschlag oder bestaetigtem Ergebnis - fuer Fachansicht und Buero gleich. */
export function ausErgebnis(ergebnis: Ergebnis | null): ErgebnisWerte {
  return {
    waagen: ergebnis?.waagen ?? [],
    boxHoehe: ergebnis?.boxHoehe ?? null,
    korbtyp: ergebnis?.korbtyp ?? null,
    fifo: ergebnis?.fifo ?? false,
    fifoRichtung: ergebnis?.fifoRichtung ?? null,
    anzahlArtikel: ergebnis?.anzahlArtikel ?? ergebnis?.waagen.length ?? 1,
  }
}

interface Eigenschaften {
  werte: ErgebnisWerte
  aendern: (werte: ErgebnisWerte) => void
  katalog: Katalog
  /** Ursprungswerte des Systems; Abweichungen werden als Korrektur markiert (P5-04). */
  vorschlag?: ErgebnisWerte | null
  /** Im Sonderfall gibt es keinen Korbtyp und keine Artikelanzahl (P7-03). */
  schlank?: boolean
}

/**
 * Bestaetigen oder Korrigieren per Tipp (P5-02): sechs Waagengroessen als Schaltflaechen,
 * Boxhoehe H8/H16 oder ohne Box, Fachteiler ja/nein sowie quer/laengs. Es gibt keine
 * Pflichteingabe ohne Vorbelegung - alle Felder kommen mit dem Vorschlag des Systems.
 *
 * <p>Korbtyp und Boxhoehe sind gekoppelt (R-03): eine Box legt den Korbtyp fest, ein Korbtyp die
 * passende Box. Nur bei "ohne Box" sind beide frei - dann bleibt die Box aus, auch wenn der
 * Korbtyp wechselt. Die Zuordnung kommt aus dem Katalog, nicht aus dem Code (R-01); das Backend
 * gleicht sie beim Speichern noch einmal ab.
 */
export default function ErgebnisFormular({ werte, aendern, katalog, vorschlag, schlank }: Eigenschaften) {
  const setze = (teil: Partial<ErgebnisWerte>) => aendern({ ...werte, ...teil })

  const anzahl = werte.anzahlArtikel ?? werte.waagen.length ?? 1

  const anzahlSetzen = (neu: number) => {
    // 0 ist zulaessig: ein leeres Fach braucht keine Waage.
    const begrenzt = Math.max(0, Math.min(6, neu))
    const waagen = [...werte.waagen]
    while (waagen.length < begrenzt) {
      waagen.push(waagen[waagen.length - 1] ?? katalog.smartScales[katalog.smartScales.length - 1].id)
    }
    waagen.length = begrenzt
    setze({ anzahlArtikel: begrenzt, waagen })
  }

  const waageSetzen = (index: number, id: string) => {
    const waagen = [...werte.waagen]
    waagen[index] = id
    setze({ waagen })
  }

  // Mit Box zieht die Box mit dem Korbtyp mit; ohne Box bleibt sie bewusst aus.
  const korbtypSetzen = (typ: Korbtyp) => {
    if (werte.boxHoehe === 'OHNE') {
      setze({ korbtyp: typ })
      return
    }
    const box = katalog.boxen.find((eintrag) => eintrag.korbtyp === typ)
    setze({ korbtyp: typ, boxHoehe: box ? (box.id as BoxHoehe) : werte.boxHoehe })
  }

  const abweichung = (feld: keyof ErgebnisWerte) => {
    if (!vorschlag) return false
    return JSON.stringify(vorschlag[feld]) !== JSON.stringify(werte[feld])
  }

  return (
    <div className="gruppe" style={{ gap: 20 }}>
      {!schlank && (
        <section className="gruppe">
          <div className="kopfzeile">
            <span className="label">Korbtyp</span>
            {abweichung('korbtyp') && <span className="sek">korrigiert</span>}
          </div>
          <div className="wahl wahl-2">
            {(['flach', 'tief'] as Korbtyp[]).map((typ) => (
              <button
                key={typ}
                className="opt"
                aria-pressed={werte.korbtyp === typ}
                onClick={() => korbtypSetzen(typ)}
              >
                {typ === 'flach' ? 'flach (100 mm)' : 'tief (200 mm)'}
              </button>
            ))}
          </div>
        </section>
      )}

      <section className="gruppe">
        <div className="kopfzeile">
          <span className="label">{schlank ? 'Anzahl Waagen' : 'Artikel im Korb'}</span>
          {abweichung('anzahlArtikel') && <span className="sek">korrigiert</span>}
        </div>
        <div className="stepper">
          <button onClick={() => anzahlSetzen(anzahl - 1)} aria-label="weniger">
            −
          </button>
          <span className="wert">{anzahl}</span>
          <button onClick={() => anzahlSetzen(anzahl + 1)} aria-label="mehr">
            +
          </button>
        </div>
      </section>

      <section className="gruppe">
        <div className="kopfzeile">
          <span className="label">Waagengröße</span>
          {abweichung('waagen') && <span className="sek">korrigiert</span>}
        </div>
        {anzahl === 0 && <p className="sek">Leeres Fach – es wird keine Waage gezählt.</p>}
        {Array.from({ length: anzahl }).map((_, index) => (
          <div key={index} className="gruppe">
            {anzahl > 1 && <span className="sek">Artikel {index + 1}</span>}
            <div className="wahl wahl-3">
              {katalog.smartScales.map((waage) => (
                <button
                  key={waage.id}
                  className="opt klein"
                  aria-pressed={werte.waagen[index] === waage.id}
                  onClick={() => waageSetzen(index, waage.id)}
                >
                  {waage.breiteCm} × {waage.laengeCm}
                </button>
              ))}
            </div>
          </div>
        ))}
      </section>

      <section className="gruppe">
        <div className="kopfzeile">
          <span className="label">Boxhöhe</span>
          {abweichung('boxHoehe') && <span className="sek">korrigiert</span>}
        </div>
        <div className="wahl wahl-3">
          {/* "ohne Box" kommt aus dem Katalog wie H8 und H16, hat aber keinen Korbtyp: der
              gewaehlte bleibt dann stehen. Die Erkennung schlaegt sie nie vor. */}
          {katalog.boxen.map((box) => (
            <button
              key={box.id}
              className="opt"
              aria-pressed={werte.boxHoehe === box.id}
              onClick={() => setze({ boxHoehe: box.id as BoxHoehe, korbtyp: box.korbtyp ?? werte.korbtyp })}
            >
              {box.korbtyp ? `${box.id} (${box.hoeheCm} cm)` : box.name}
            </button>
          ))}
        </div>
      </section>

      <section className="gruppe">
        <div className="kopfzeile">
          <span className="label">FiFo-Einsatz</span>
          {/* Die Richtung gehoert zu diesem Abschnitt und wird mitgeprueft: ohne sie bliebe eine
              geaenderte Richtung als einzige Korrektur unmarkiert, obwohl der Vorschlag darueber
              noch die urspruengliche zeigt. */}
          {(abweichung('fifo') || abweichung('fifoRichtung')) && <span className="sek">korrigiert</span>}
        </div>
        <div className="wahl wahl-2">
          <button
            className="opt"
            aria-pressed={!werte.fifo}
            onClick={() => setze({ fifo: false, fifoRichtung: null })}
          >
            ohne
          </button>
          <button
            className="opt"
            aria-pressed={werte.fifo}
            onClick={() => setze({ fifo: true, fifoRichtung: werte.fifoRichtung ?? 'quer' })}
          >
            mit Teiler
          </button>
        </div>
        {werte.fifo && (
          <div className="wahl wahl-2">
            {(['quer', 'laengs'] as FifoRichtung[]).map((richtung) => (
              <button
                key={richtung}
                className="opt klein"
                aria-pressed={werte.fifoRichtung === richtung}
                onClick={() => setze({ fifoRichtung: richtung })}
              >
                {richtung === 'quer' ? 'quer' : 'längs'}
              </button>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}
